import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  AutoStories,
  Delete,
  DeleteOutline,
  Send,
} from "@mui/icons-material";
import {
  createHadiths,
  deleteHadith,
  getHadiths,
} from "../../context/Religious";

const emptyRow = { arabic: "", translation: "", reference: "", narrator: "" };

const startOfToday = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

export default function HadithComponent() {
  const theme = useTheme();

  const [hadiths, setHadiths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Multi-add rows
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchHadiths = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getHadiths();
      setHadiths(data.hadiths || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load hadiths");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHadiths();
  }, []);

  const updateRow = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, { ...emptyRow }]);

  const removeRow = (index) =>
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const handleSubmit = async () => {
    const cleaned = rows
      .map((r) => ({
        arabic: r.arabic.trim(),
        translation: r.translation.trim(),
        reference: r.reference.trim(),
        narrator: r.narrator.trim(),
      }))
      .filter((r) => r.translation && r.reference);

    if (cleaned.length === 0) {
      showSnackbar("Add at least one hadith with a translation and a reference.", "error");
      return;
    }

    try {
      setSaving(true);
      const { count } = await createHadiths(cleaned);
      showSnackbar(
        `${count} hadith${count > 1 ? "s" : ""} scheduled — one per day starting from the next free day.`
      );
      setRows([{ ...emptyRow }]);
      await fetchHadiths();
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || "Failed to schedule hadiths.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteHadith(deleteDialog.id);
      setHadiths((prev) => prev.filter((h) => h._id !== deleteDialog.id));
      showSnackbar("Hadith deleted.");
      setDeleteDialog({ open: false, id: null });
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const today = startOfToday().getTime();

  const dayLabel = (iso) => {
    const d = new Date(iso);
    const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).getTime();
    if (day === today) return { label: "Today", color: "success" };
    if (day < today) return { label: "Delivered", color: "default" };
    return { label: "Upcoming", color: "info" };
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" color={theme.palette.text.primary}>
          Daily Hadith
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload one or more hadiths — each is delivered to users one day at a time. Upload three
          today and users see one each of the next three days.
        </Typography>
      </Box>

      {/* Composer */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: theme.palette.background.paper, boxShadow: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" mb={2} color={theme.palette.text.primary}>
          Add Hadith(s)
        </Typography>

        {rows.map((row, index) => (
          <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < rows.length - 1 ? `1px solid ${theme.palette.divider}` : "none" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Hadith {index + 1}
              </Typography>
              {rows.length > 1 && (
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => removeRow(index)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <TextField
              label="Arabic (optional)"
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              dir="rtl"
              value={row.arabic}
              onChange={updateRow(index, "arabic")}
            />
            <TextField
              label="Translation *"
              fullWidth
              margin="dense"
              multiline
              minRows={2}
              value={row.translation}
              onChange={updateRow(index, "translation")}
            />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Reference *"
                sx={{ flex: 1, minWidth: 200 }}
                margin="dense"
                placeholder="e.g. Sahih al-Bukhari 5063"
                value={row.reference}
                onChange={updateRow(index, "reference")}
              />
              <TextField
                label="Narrator (optional)"
                sx={{ flex: 1, minWidth: 200 }}
                margin="dense"
                placeholder="e.g. Narrated by Anas ibn Malik"
                value={row.narrator}
                onChange={updateRow(index, "narrator")}
              />
            </Box>
          </Box>
        ))}

        <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<Add />} onClick={addRow} disabled={saving}>
            Add Another
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Scheduling…" : "Schedule Hadith(s)"}
          </Button>
        </Box>
      </Paper>

      {/* Schedule list */}
      <Typography variant="h6" fontWeight="bold" mb={2} color={theme.palette.text.primary}>
        Schedule
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: "30vh", alignItems: "center" }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : hadiths.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", backgroundColor: theme.palette.background.paper }}>
          <AutoStories sx={{ fontSize: 56, color: theme.palette.text.secondary, opacity: 0.4, mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No hadiths scheduled yet
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Delivery Day</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Translation</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hadiths.map((h) => {
                const status = dayLabel(h.scheduledDate);
                return (
                  <TableRow key={h._id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(h.scheduledDate)}</TableCell>
                    <TableCell>
                      <Chip label={status.label} size="small" color={status.color} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Typography variant="body2" noWrap title={h.translation}>
                        {h.translation}
                      </Typography>
                      {h.narrator && (
                        <Typography variant="caption" color="text.secondary">
                          {h.narrator}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{h.reference}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: h._id })}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Delete Hadith</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this hadith from the schedule? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
